import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Textarea } from '@components';
import { FaqApiError } from '../services/faqService';
import { CompanyInfoTopic, FAQ_ANSWER_MAX_LENGTH } from '../types';

interface CompanyInfoModalProps {
  // The modal is open while a topic is set
  topic: CompanyInfoTopic | null;
  // Empty for a topic that is not filled in yet
  answer: string;
  onClose: () => void;
  onSubmit: (topic: CompanyInfoTopic, answer: string) => Promise<unknown>;
  isSaving: boolean;
}

export const CompanyInfoModal: React.FC<CompanyInfoModalProps> = ({
  topic,
  answer,
  onClose,
  onSubmit,
  isSaving,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (topic) {
      setValue(answer);
      setError(null);
    }
  }, [topic, answer]);

  if (!topic) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await onSubmit(topic, value.trim());
      onClose();
    } catch (err) {
      if (err instanceof FaqApiError && err.fieldErrors[topic]) {
        setError(err.fieldErrors[topic]);
      } else {
        setError(err instanceof Error ? err.message : t('knowledgeBase.companyInfo.saveFailed'));
      }
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t(`knowledgeBase.companyInfo.topics.${topic}.label`)}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-1 h-8 w-8">
            <span className="material-icons">close</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Textarea
            id={`company-info-${topic}`}
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={t(`knowledgeBase.companyInfo.topics.${topic}.placeholder`)}
            // Topics that overlap with the scheduling calendar say what belongs where
            helperText={t(`knowledgeBase.companyInfo.topics.${topic}.hint`, {
              defaultValue: t('knowledgeBase.companyInfo.answerHelp'),
            })}
            rows={6}
            maxLength={FAQ_ANSWER_MAX_LENGTH}
            showCount
            required
            state={error ? 'error' : 'default'}
            errorText={error ?? undefined}
          />

          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="gradient-multi"
              className="flex items-center gap-2"
              disabled={value.trim() === '' || isSaving}
            >
              <span className="material-icons">save</span>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
